/**
 * DeferredGateTrackingPage — Compliance & Capture · Deferred Gate Tracking.
 *
 * Per-cart deferred-N-days gate deadlines (Bill 2026-05-11 admin-configurable
 * gates). The runtime auto-creates a row whenever an EntityRuleGateEnforcement
 * strategy resolves to `deferred-N-days-post-acctadd`; this page lets admins
 * view / satisfy / escalate deferrals across all carts and insert manual
 * override rows when needed.
 *
 * Contract:
 *   - Read:  GET /v3/admin/deferred-gate-tracking?includeInactive=… (bearer)
 *            dropdowns: config.gateKeys() + config.procedureCitations()
 *   - Write: POST   /v3/admin/deferred-gate-tracking            (upsert)
 *            DELETE /v3/admin/deferred-gate-tracking/{id}       (soft-delete)
 *
 * Migrated 2026-06-16 from ROP-teams Admin (operational app writes no config).
 * Auth: ADMIN_AUTH_TOKEN_SENTINEL → buildAdminAuthHeaders OMITS the bypass-key
 * (MSAL bearer carries authority; no secret in the prod bundle).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ADMIN_AUTH_TOKEN_SENTINEL,
  ApiError,
  buildAdminAuthHeaders,
} from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { TextField, SelectField, TextArea, BoolField } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';

const STATUSES = ['pending', 'satisfied', 'escalated', 'expired'] as const;
type Status = (typeof STATUSES)[number];

interface Row {
  trackingId: string;
  accountNumber: string | null;
  cifNumber: string | null;
  gateKey: string;
  cartId: string | null;
  deferralReason: string;
  deferredAt: string;
  deadlineAt: string;
  satisfiedAt: string | null;
  escalatedAt: string | null;
  status: Status;
  citationKey: string;
  notes: string | null;
  officerCode: string | null;
  isActive: boolean;
}

interface GateKeyOpt {
  key: string;
}
interface CitationOpt {
  citationKey: string;
  // Wire field is `displayLabel` (ProcedureCitation entity); was read as `title`
  // → dropdown rendered "KEY — undefined" (BACKLOG-0579 verify fix 2026-06-17).
  displayLabel: string;
}

const STATUS_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'All statuses' },
  ...STATUSES.map((s) => ({ value: s, label: s })),
];

const EDIT_STATUS_OPTIONS: ReadonlyArray<{ value: string; label: string }> = STATUSES.map((s) => ({
  value: s,
  label: s,
}));

function todayPlusDaysLocal(d: number): string {
  const t = new Date();
  t.setDate(t.getDate() + d);
  return t.toISOString().slice(0, 16);
}

function emptyRow(): Row {
  return {
    trackingId: '',
    accountNumber: '',
    cifNumber: '',
    gateKey: '',
    cartId: null,
    deferralReason: '',
    deferredAt: new Date().toISOString().slice(0, 16),
    deadlineAt: todayPlusDaysLocal(30),
    satisfiedAt: null,
    escalatedAt: null,
    status: 'pending',
    citationKey: '',
    notes: '',
    officerCode: '',
    isActive: true,
  };
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}

function isOverdue(r: Row): boolean {
  return r.status === 'pending' && new Date(r.deadlineAt) < new Date();
}

function statusPill(r: Row): JSX.Element {
  if (r.status === 'pending') {
    return isOverdue(r) ? <Pill tone="err">Overdue</Pill> : <Pill tone="warn">Pending</Pill>;
  }
  if (r.status === 'satisfied') return <Pill tone="ok">Satisfied</Pill>;
  if (r.status === 'escalated') return <Pill tone="err">Escalated</Pill>;
  return <Pill tone="muted">Expired</Pill>;
}

export function DeferredGateTrackingPage(): JSX.Element {
  const client = useApiClient();
  const [rows, setRows] = useState<ReadonlyArray<Row> | null>(null);
  const [gates, setGates] = useState<ReadonlyArray<GateKeyOpt>>([]);
  const [citations, setCitations] = useState<ReadonlyArray<CitationOpt>>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [edit, setEdit] = useState<Row | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ tone: 'info' | 'warn'; text: string } | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setRows(null);
      setLoadError(null);
      try {
        const [r, g, c] = await Promise.all([
          client.http.get<{ success: boolean; data?: ReadonlyArray<Row>; error?: string }>(
            `/v3/admin/deferred-gate-tracking?includeInactive=${showInactive ? 'true' : 'false'}`,
          ),
          client.retail.config.gateKeys() as unknown as Promise<{
            success: boolean;
            data?: ReadonlyArray<GateKeyOpt>;
          }>,
          client.retail.config.procedureCitations() as unknown as Promise<{
            success: boolean;
            data?: ReadonlyArray<CitationOpt>;
          }>,
        ]);
        if (cancelled) return;
        if (r.success && Array.isArray(r.data)) setRows(r.data);
        else setLoadError(r.error ?? 'Failed to load deferred-gate tracking.');
        if (g.success && Array.isArray(g.data)) setGates(g.data);
        if (c.success && Array.isArray(c.data)) setCitations(c.data);
      } catch (e) {
        if (cancelled) return;
        setLoadError(
          ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e),
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, showInactive, refreshNonce]);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  const filtered = useMemo(() => {
    if (!rows) return rows;
    return filterStatus ? rows.filter((r) => r.status === filterStatus) : rows;
  }, [rows, filterStatus]);

  const overdueCount = useMemo(() => (rows ?? []).filter(isOverdue).length, [rows]);

  const writeHeaders = useCallback(
    () => buildAdminAuthHeaders(undefined, ADMIN_AUTH_TOKEN_SENTINEL),
    [],
  );

  const save = useCallback(
    async (row: Row): Promise<boolean> => {
      try {
        const body = {
          ...row,
          trackingId: row.trackingId || undefined,
          accountNumber: row.accountNumber?.trim() || null,
          cifNumber: row.cifNumber?.trim() || null,
          cartId: row.cartId || null,
          notes: row.notes?.trim() || null,
          officerCode: row.officerCode?.trim() || null,
          satisfiedAt: row.satisfiedAt || null,
        };
        const r = await client.http.post<{ success: boolean; error?: string }>(
          '/v3/admin/deferred-gate-tracking',
          { headers: writeHeaders(), body },
        );
        if (!r.success) throw new Error(r.error ?? 'save failed');
        return true;
      } catch (e) {
        setSaveError(
          ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e),
        );
        return false;
      }
    },
    [client, writeHeaders],
  );

  const handleSatisfy = useCallback(
    async (row: Row) => {
      setSaving(true);
      const ok = await save({ ...row, status: 'satisfied', satisfiedAt: new Date().toISOString() });
      setSaving(false);
      if (ok) {
        setToast({ tone: 'info', text: `Marked ${row.gateKey} deferral satisfied.` });
        refresh();
      }
    },
    [save, refresh],
  );

  const handleDelete = useCallback(
    async (row: Row) => {
      setSaving(true);
      setSaveError(null);
      try {
        const r = await client.http.del<{ success: boolean; error?: string }>(
          `/v3/admin/deferred-gate-tracking/${encodeURIComponent(row.trackingId)}`,
          { headers: writeHeaders() },
        );
        if (!r.success) throw new Error(r.error ?? 'delete failed');
        setToast({ tone: 'warn', text: `Soft-deleted deferral ${row.trackingId.slice(0, 8)}.` });
        refresh();
      } catch (e) {
        setSaveError(
          ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e),
        );
      } finally {
        setSaving(false);
      }
    },
    [client, writeHeaders, refresh],
  );

  const handleSaveEdit = useCallback(async () => {
    if (!edit) return;
    if (!edit.gateKey || !edit.deferralReason || !edit.citationKey || !edit.deadlineAt) {
      setSaveError('Gate, reason, citation, and deadline are all required.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    const ok = await save(edit);
    setSaving(false);
    if (ok) {
      setToast({ tone: 'info', text: isNew ? 'Created manual deferral.' : 'Saved deferral.' });
      setEdit(null);
      refresh();
    }
  }, [edit, isNew, save, refresh]);

  const columns: ReadonlyArray<DataTableColumn<Row>> = useMemo(
    () => [
      { header: 'Status', cell: (r) => statusPill(r) },
      { header: 'Gate', cell: (r) => <code className="cell-mono">{r.gateKey}</code> },
      {
        header: 'Account / CIF',
        cell: (r) => (
          <span className="cell-desc">
            <code className="cell-mono">{r.accountNumber || '—'}</code> /{' '}
            <code className="cell-mono">{r.cifNumber || '—'}</code>
          </span>
        ),
      },
      {
        header: 'Deadline',
        cell: (r) => (
          <span className={isOverdue(r) ? 'stat-err' : 'cell-desc'}>{fmtDate(r.deadlineAt)}</span>
        ),
      },
      {
        header: 'Reason',
        cell: (r) => (
          <span className="cell-desc" title={r.deferralReason}>
            {r.deferralReason.length > 48 ? `${r.deferralReason.slice(0, 48)}…` : r.deferralReason}
          </span>
        ),
      },
      { header: 'Officer', cell: (r) => <code className="cell-mono">{r.officerCode || '—'}</code> },
      {
        header: '',
        cell: (r) => (
          <div className="card-actions-row">
            <Button
              onClick={() => {
                setEdit({
                  ...r,
                  accountNumber: r.accountNumber ?? '',
                  cifNumber: r.cifNumber ?? '',
                  notes: r.notes ?? '',
                  officerCode: r.officerCode ?? '',
                });
                setIsNew(false);
                setSaveError(null);
              }}
            >
              Edit
            </Button>
            {r.status === 'pending' && (
              <Button variant="primary" onClick={() => void handleSatisfy(r)} disabled={saving}>
                Satisfy
              </Button>
            )}
            <Button onClick={() => void handleDelete(r)} disabled={saving}>
              Delete
            </Button>
          </div>
        ),
      },
    ],
    [saving, handleSatisfy, handleDelete],
  );

  if (loadError) {
    return (
      <Banner tone="err">
        Could not load deferred-gate tracking: {loadError} <Button onClick={refresh}>Retry</Button>
      </Banner>
    );
  }
  if (!rows) return <Spinner label="Loading deferred-gate tracking…" />;

  return (
    <>
      {toast && (
        <Banner tone={toast.tone}>
          {toast.text} <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}

      <Card
        title={`Deferred Gate Tracking — ${rows.length} rows${overdueCount > 0 ? ` · ⚠ ${overdueCount} overdue` : ''}`}
        actions={
          <div className="card-actions-row">
            <div className="users-filter">
              <SelectField
                label=""
                value={filterStatus}
                onChange={setFilterStatus}
                options={STATUS_OPTIONS}
              />
            </div>
            <BoolField label="Show inactive" value={showInactive} onChange={setShowInactive} />
            <Button
              variant="primary"
              onClick={() => {
                setEdit(emptyRow());
                setIsNew(true);
                setSaveError(null);
              }}
            >
              + Manual deferral
            </Button>
          </div>
        }
      >
        <DataTable<Row>
          columns={columns}
          rows={filtered ?? []}
          rowKey={(r) => r.trackingId}
          emptyState={<p className="empty">No deferrals match the current filter.</p>}
        />
      </Card>

      <ModalShell
        open={edit !== null}
        title={isNew ? 'New deferral (manual override)' : `Edit deferral ${edit?.trackingId.slice(0, 8) ?? ''}`}
        onClose={() => {
          if (saving) return;
          setEdit(null);
          setSaveError(null);
        }}
        width={680}
        actions={
          edit && (
            <>
              <Button onClick={() => setEdit(null)} disabled={saving}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => void handleSaveEdit()} disabled={saving}>
                {saving ? 'Saving…' : isNew ? 'Create' : 'Save changes'}
              </Button>
            </>
          )
        }
      >
        {edit && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}

            <div className="form-row">
              <SelectField
                label="Gate key"
                value={edit.gateKey}
                onChange={(v) => setEdit({ ...edit, gateKey: v })}
                disabled={!isNew}
                options={[
                  { value: '', label: '— pick gate —' },
                  ...gates.map((g) => ({ value: g.key, label: g.key })),
                ]}
                hint={isNew ? 'The enforcement gate this deferral satisfies.' : 'Gate is fixed once created.'}
              />
              <SelectField
                label="Status"
                value={edit.status}
                onChange={(v) => setEdit({ ...edit, status: v as Status })}
                options={EDIT_STATUS_OPTIONS}
              />
            </div>

            <div className="form-row">
              <TextField
                label="Account number"
                value={edit.accountNumber ?? ''}
                onChange={(v) => setEdit({ ...edit, accountNumber: v })}
                placeholder="9-digit acct #"
                monospace
              />
              <TextField
                label="CIF number"
                value={edit.cifNumber ?? ''}
                onChange={(v) => setEdit({ ...edit, cifNumber: v })}
                placeholder="9-digit CIF #"
                monospace
              />
            </div>

            <div className="form-row">
              <label className="form-field">
                <span className="form-field__label">Deadline (local)</span>
                <input
                  type="datetime-local"
                  className="form-input"
                  aria-label="Deadline"
                  value={edit.deadlineAt.slice(0, 16)}
                  onChange={(e) => setEdit({ ...edit, deadlineAt: e.target.value })}
                />
                <span className="form-field__hint">
                  When the deferred requirement must be satisfied before the gate hard-stops.
                </span>
              </label>
              <TextField
                label="Officer code"
                value={edit.officerCode ?? ''}
                onChange={(v) => setEdit({ ...edit, officerCode: v })}
                placeholder="WW / BWIG"
                monospace
              />
            </div>

            <TextArea
              label="Deferral reason"
              value={edit.deferralReason}
              onChange={(v) => setEdit({ ...edit, deferralReason: v })}
              rows={2}
              placeholder="Customer promised to bring proof of address Tuesday."
            />

            <SelectField
              label="Citation key"
              value={edit.citationKey}
              onChange={(v) => setEdit({ ...edit, citationKey: v })}
              options={[
                { value: '', label: '— pick citation —' },
                ...citations.map((c) => ({ value: c.citationKey, label: `${c.citationKey} — ${c.displayLabel}` })),
              ]}
              hint="The procedure citation authorizing this deferral (DO-NOT-GUESS — every deferral cites legacy procedure)."
            />

            <TextArea
              label="Notes (lifecycle log)"
              value={edit.notes ?? ''}
              onChange={(v) => setEdit({ ...edit, notes: v })}
              rows={2}
              placeholder="2026-05-13 — customer called, will come Friday instead."
            />

            <BoolField
              label="Active"
              value={edit.isActive}
              onChange={(v) => setEdit({ ...edit, isActive: v })}
              hint="Inactive rows are retained for audit but no longer enforced."
            />
          </div>
        )}
      </ModalShell>
    </>
  );
}
